/**
 * Role Check Utility
 * Helper functions for checking user roles on the frontend
 */

/**
 * Check if user has a specific role
 * @param {Object} user - User object (from localStorage or session)
 * @param {string} roleName - Name of the role to check
 * @returns {boolean} True if user has the role
 */
export const hasRole = (user, roleName) => {
  if (!user) {
    return false;
  }

  // Check if user has roles array
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some(role => role.roleName === roleName || role === roleName);
  }

  // Check if roles are stored as roleNames array
  if (user.roleNames && Array.isArray(user.roleNames)) {
    return user.roleNames.includes(roleName);
  }

  return false;
};

/**
 * Check if user has any of the specified roles
 * @param {Object} user - User object
 * @param {Array<string>} roleNames - Array of role names
 * @returns {boolean} True if user has at least one of the roles
 */
export const hasAnyRole = (user, roleNames) => {
  return roleNames.some(roleName => hasRole(user, roleName));
};

/**
 * Check if user has all of the specified roles
 * @param {Object} user - User object
 * @param {Array<string>} roleNames - Array of role names
 * @returns {boolean} True if user has all the roles
 */
export const hasAllRoles = (user, roleNames) => {
  return roleNames.every(roleName => hasRole(user, roleName));
};

/**
 * Get current user from storage
 * Checks both 'user' and 'userData' keys in localStorage
 * @returns {Object|null} User object or null
 */
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('user') || localStorage.getItem('userData');
    if (userData) {
      return JSON.parse(userData);
    }
    return null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Check if current user has a specific role
 * @param {string} roleName - Name of the role to check
 * @returns {boolean} True if current user has the role
 */
export const currentUserHasRole = (roleName) => {
  const user = getCurrentUser();
  return hasRole(user, roleName);
};

/**
 * Higher-Order Component to protect routes based on roles
 * @param {React.Component} Component - Component to protect
 * @param {string|Array<string>} requiredRoles - Required role(s)
 * @param {React.Component} FallbackComponent - Component to show if access denied
 * @returns {React.Component} Protected component
 */
export const requireRole = (Component, requiredRoles, FallbackComponent = null) => {
  return (props) => {
    const user = getCurrentUser();
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    const hasAccess = hasAnyRole(user, roles);

    if (!hasAccess) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view this page.</p>
        </div>
      );
    }

    return <Component {...props} />;
  };
};

/**
 * Store user roles in local storage (called after fetching user details)
 * @param {string} userId - User ID
 * @param {Array} roles - Array of role objects
 */
export const storeUserRoles = (userId, roles) => {
  try {
    const user = getCurrentUser();
    if (user) {
      user.roles = roles;
      user.roleNames = roles.map(r => r.roleName);
      localStorage.setItem('user', JSON.stringify(user));
    }
  } catch (error) {
    console.error('Error storing user roles:', error);
  }
};

/**
 * Clear user roles from storage (called on logout)
 */
export const clearUserRoles = () => {
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('userData');
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionId');
  } catch (error) {
    console.error('Error clearing user data:', error);
  }
};

/**
 * Check if user has UserAdmin role
 * @param {Array} roles - Array of role objects or role names
 * @returns {boolean} True if user has UserAdmin role
 */
export const hasUserAdminRole = (roles) => {
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some(role => {
    if (typeof role === 'string') {
      return role === 'UserAdmin';
    }
    return role.roleName === 'UserAdmin';
  });
};

/**
 * Check if user has CompanyAdmin role
 * @param {Array} roles - Array of role objects or role names
 * @returns {boolean} True if user has CompanyAdmin role
 */
export const hasCompanyAdminRole = (roles) => {
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some(role => {
    if (typeof role === 'string') {
      return role === 'CompanyAdmin';
    }
    return role.roleName === 'CompanyAdmin';
  });
};

/**
 * Check if user has CerebralAdmin role
 * @param {Array} roles - Array of role objects or role names
 * @returns {boolean} True if user has CerebralAdmin role
 */
export const hasCerebralAdminRole = (roles) => {
  if (!roles || !Array.isArray(roles)) {
    return false;
  }
  return roles.some(role => {
    if (typeof role === 'string') {
      return role === 'CerebralAdmin';
    }
    return role.roleName === 'CerebralAdmin';
  });
};

/**
 * Check if current user has CerebralAdmin role
 * @returns {boolean} True if current user has CerebralAdmin role
 */
export const currentUserHasCerebralAdminRole = () => {
  const user = getCurrentUser();
  if (!user) return false;
  
  if (user.roles && Array.isArray(user.roles)) {
    return hasCerebralAdminRole(user.roles);
  }
  
  if (user.roleNames && Array.isArray(user.roleNames)) {
    return user.roleNames.includes('CerebralAdmin');
  }
  
  return false;
};

/**
 * Check if user has any of the specified roles
 * @param {Array} userRoles - Array of role objects or role names
 * @param {Array<string>} requiredRoles - Array of required role names
 * @returns {boolean} True if user has at least one of the required roles
 */
export const hasRoles = (userRoles, requiredRoles) => {
  if (!userRoles || !Array.isArray(userRoles) || !requiredRoles || !Array.isArray(requiredRoles)) {
    return false;
  }
  
  return requiredRoles.some(requiredRole => {
    return userRoles.some(role => {
      if (typeof role === 'string') {
        return role === requiredRole;
      }
      return role.roleName === requiredRole;
    });
  });
};


